import { useState } from 'react';

import { theme } from '@prose-ui';
import { keyframes, legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import { Button } from 'Components/LegacyButton';
import { Typography } from 'Components/Typography';

import { moduleCapsule } from 'assets/content/landingPages/supplements';
import { ReactComponent as CircleSVG } from 'assets/images/landingPages/supplements/supplements_capsule_circle.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { CTA } from './CTA';
import { LineSeparator } from './LineSeparator';

const Root = styled.section`
  position: relative;

  width: 100vw;
  padding: 104px 24px 56px;

  background-color: ${theme.colors.supps[200]};
  border-bottom: 2px solid ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 104px 72px 88px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    padding: 104px 135px 114px;
  }
`;

const TitleWrapper = styled.div`
  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 59px;
  }
`;

const Title = styled(Typography)`
  margin-bottom: 39.5px;

  font-size: 36px;
  line-height: 1.17;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 48px;
    font-size: 60px;
    font-weight: normal;
    letter-spacing: normal;
  }

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 669px;
    margin-bottom: 0;
  }
`;

const CircleWrapper = styled.div`
  position: absolute;
  top: -40px;
  right: 23px;
  transform: translateY(-50%);
  z-index: 10;

  width: 105px;

  cursor: pointer;

  ${legacyTheme.breakpoints.up('sm')} {
    right: 45px;
    width: 130px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    position: relative;
    top: 0;
    right: 10px;
    transform: translateY(0%);
  }
`;

const rotation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Circle = styled(CircleSVG)`
  animation: ${rotation} 10s linear infinite;
`;

const Block = styled.div`
  margin-bottom: 51px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 80px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 69px;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const Content = styled.div`
  margin-top: 47.5px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-top: 64px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    justify-content: space-between;
    margin-top: 59px;
  }
`;

const TextWrapper = styled.div`
  ${legacyTheme.breakpoints.up('md')} {
    width: 100%;
    max-width: 269px;
    margin-right: 50px;
  }

  ${legacyTheme.breakpoints.up('lg')} {
    margin-right: 131px;
  }
`;

const Capsule = styled(Typography)`
  margin-bottom: 8px;

  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.5px;

  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 16px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 12px;
  }
`;

const Number = styled(Typography)`
  font-size: 57px;
  font-weight: normal;
  line-height: 1;
  letter-spacing: normal;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 8px;
    font-size: 60px;
  }
`;

const Info = styled(Typography)`
  margin-bottom: 16px;

  font-size: 24px;
  font-weight: normal;
  line-height: normal;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('sm')} {
    line-height: 1.33;
    letter-spacing: normal;
  }
`;

const Description = styled(Typography)`
  margin-bottom: 32px;

  font-weight: normal;
  line-height: 1.38;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 18px;
    font-weight: normal;
    line-height: 1.33;
    letter-spacing: normal;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 24px;
  }

  & sup {
    top: -0.3em;
  }

  & sup#pure-catalyst-tm {
    top: -0.5em;
    font-size: 60%;
    font-weight: 500;
  }
`;

const Legend = styled(Typography)`
  display: flex;
  align-items: center;
  margin-top: -8px;
  margin-bottom: 32px;

  letter-spacing: 0.5px;

  ${legacyTheme.breakpoints.up('md')} {
    flex-direction: column;
    align-items: flex-start;
    margin-top: 0;
    margin-bottom: 40px;
  }

  & a {
    font-weight: 600;
    text-decoration: none;
  }
`;

const LegendText = styled(Typography)`
  letter-spacing: 0.5px;

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 8px;
  }
`;

const Images = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ImageWrapper = styled.div`
  width: calc(50% - 8px);

  ${legacyTheme.breakpoints.up('sm')} {
    width: calc(50% - 12px);
  }

  ${legacyTheme.breakpoints.up('md')} {
    width: calc(50% - 15.5px);
  }
`;

const Image = styled.img`
  width: 100%;
  height: auto;
`;

const useStyles = makeStyles({
  clinicalStudy: {
    margin: '0',
    marginLeft: '5px',

    fontFamily: `${legacyTheme.typography.simplonNorm}`,
    fontSize: '12px',
    textTransform: 'initial',
    lineHeight: 0.6,
    letterSpacing: '0.5px',

    '&::after': {
      height: '1px',
    },

    [legacyTheme.breakpoints.up('md')]: {
      marginLeft: 0,
    },
  },
});

type ModuleCapsuleProps = {
  showCircle?: boolean;
};

export const ModuleCapsule = ({ showCircle = false }: ModuleCapsuleProps) => {
  const { isDesktop } = useResponsiveVariant();
  const { classes } = useStyles();

  // For accessibility purpose: infinite animation should be stoppable
  const [animationRunning, setAnimationRunning] = useState(true);

  return (
    <Root data-testid="supplements-landing-module">
      <TitleWrapper>
        <Title markupName="p" variant="p1">
          {moduleCapsule.title}
        </Title>

        {showCircle && (
          <CircleWrapper
            onClick={() => {
              setAnimationRunning(!animationRunning);
            }}
            onKeyPress={() => {
              setAnimationRunning(!animationRunning);
            }}
            tabIndex={0}
          >
            <Circle
              // For accessibility purpose: infinite animation should be stoppable
              style={{
                animation: !animationRunning ? 'none' : undefined,
              }}
            />
          </CircleWrapper>
        )}
      </TitleWrapper>

      {moduleCapsule.body.map((el) => (
        <Block key={el.number}>
          <LineSeparator />

          <Content>
            <TextWrapper>
              <Capsule markupName="p" upperCase variant="mono1">
                {el.capsule}
              </Capsule>

              <Number markupName="p" variant="p1">
                {el.number}
              </Number>

              <Info markupName="p" variant="p1">
                {el.info}
              </Info>

              <Description markupName="p" variant="p1">
                {el.description}
              </Description>

              {el.legend && (
                <Legend markupName="p" variant="p3">
                  <LegendText markupName="span" variant="p3">
                    {el.legend.text}
                  </LegendText>
                  <Button
                    classes={{
                      underlinedBlack: classes.clinicalStudy ?? '',
                    }}
                    Component="a"
                    data-testid="supplements-clinical-study"
                    href={el.legend.linkPdf}
                    target="_blank"
                    variant="underlinedBlack"
                  >
                    {el.legend.linkText}
                  </Button>
                </Legend>
              )}
              {el.cta && isDesktop && <CTA buttonVariant="whiteFull" />}
            </TextWrapper>

            <Images>
              {/* Wrapper needed to keep proportions in responsive */}
              <ImageWrapper>
                <Image alt={el.image1.alt} src={el.image1[!isDesktop ? 'mobile' : 'desktop'].src} />
              </ImageWrapper>
              <ImageWrapper>
                <Image alt={el.image2.alt} src={el.image2[!isDesktop ? 'mobile' : 'desktop'].src} />
              </ImageWrapper>
            </Images>
          </Content>
        </Block>
      ))}
    </Root>
  );
};
